/**
 * AssignmentAnnotationsSection component.
 *
 * This component renders the assignment annotations section of the
 * InferenceModelsForSlides component. It displays annotation assignments
 * organized by stain type and handles CRUD operations for assignment overrides.
 */

import { Divider, Grid, List, ListItem, Typography } from '@mui/material';
import { isEmpty, map, pickBy, size } from 'lodash';
import React from 'react';

import { inputCategoryLabels } from 'components/StudyDashboard/ProceduresPage/Actions/PostProcessingAndCalculateFeatures/calculateFeaturesInputs';
import { AnnotationAssignment } from 'interfaces/annotation';
import { AssignmentByStain, SelectedAssignmentDetails } from 'utils/assignmentManagement';
import AssignmentStain from './AssignmentStain';

export interface AssignmentAnnotationsSectionProps {
  assignmentAnnotationsByStainType: Record<string, AnnotationAssignment[]>;
  selectedAssignments: AssignmentByStain;
  addNewOverrideAssignment: (stain: string, newAssignment: SelectedAssignmentDetails) => void;
  updateSelectedAssignment: (stain: string, updatedAssignment: SelectedAssignmentDetails) => void;
  removeSelectedAssignment: (stain: string, assignmentOverrideId: string) => void;
}

/**
 * Component for rendering the assignment annotations section.
 *
 * This component displays annotation assignments organized by stain type and
 * provides controls for managing assignment overrides. It handles the presentation
 * layer for assignment selection and CRUD operations.
 */
const AssignmentAnnotationsSection: React.FC<AssignmentAnnotationsSectionProps> = ({
  assignmentAnnotationsByStainType,
  selectedAssignments,
  addNewOverrideAssignment,
  updateSelectedAssignment,
  removeSelectedAssignment,
}) => {
  const filteredAssignments = pickBy(
    assignmentAnnotationsByStainType,
    (annotationAssignments) => !isEmpty(annotationAssignments)
  );

  return (
    <Grid item container spacing={1}>
      <Grid item xs={12}>
        <Grid container direction="column" spacing={0.5}>
          <Grid item>
            <Typography variant="subtitle1" fontWeight={700}>
              {inputCategoryLabels.assignment_inputs}:
            </Typography>
          </Grid>
          {!isEmpty(filteredAssignments) && (
            <Grid item>
              <Typography variant="body2" color="textSecondary">
                Select annotation assignments to be used for one of the following:
              </Typography>
              <List dense>
                <ListItem>
                  <Typography variant="body2" color="textSecondary">
                    - Treat annotations as a source of truth
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant="body2" color="textSecondary">
                    - Manual correction of model results
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant="body2" color="textSecondary">
                    - ROIs for inclusion/exclusion based on annotations
                  </Typography>
                </ListItem>
              </List>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={1} direction="column">
          {map(filteredAssignments, (annotationAssignment, stain) => (
            <Grid item key={stain}>
              <AssignmentStain
                stain={stain}
                annotationAssignments={annotationAssignment}
                selectedAssignments={selectedAssignments[stain]}
                addNewOverrideAssignment={(newAssignment) => addNewOverrideAssignment(stain, newAssignment)}
                updateSelectedAssignment={(updatedAssignment) => updateSelectedAssignment(stain, updatedAssignment)}
                removeSelectedAssignment={(assignmentOverrideId) =>
                  removeSelectedAssignment(stain, assignmentOverrideId)
                }
                defaultExpanded={size(filteredAssignments) === 1}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
    </Grid>
  );
};

export default AssignmentAnnotationsSection;
